/**
 * @internal
 */
import React, { createContext, useContext, useEffect, useRef } from 'react';
import { motionValue, useScroll } from 'framer-motion';
import useMutableValue, { mutableValue, } from '@polestar/component-warehouse-react/hooks/useMutableValue';
import useWindowInnerHeight from '@polestar/component-warehouse-react/hooks/useWindowInnerHeight';
export var ScrollDirection;
(function (ScrollDirection) {
    ScrollDirection[ScrollDirection["up"] = -1] = "up";
    ScrollDirection[ScrollDirection["down"] = 1] = "down";
})(ScrollDirection || (ScrollDirection = {}));
export const ContentElementContext = createContext({
    contentRef: null,
    windowHeight: motionValue(0),
    scrollY: motionValue(0),
    scrollYDelta: mutableValue(0),
    scrollDirection: mutableValue(1),
});
export const useContentElement = () => useContext(ContentElementContext);
const ContentElementProvider = ({ children }) => {
    const contentRef = useRef(null);
    // Safari on iOS / iPadOS hides the address bar after scrolling
    // which provides another inner height before / after
    const windowHeight = useWindowInnerHeight({ motionValue: true });
    const { scrollY } = useScroll();
    const previousScrollY = useRef(0);
    const dragging = useMutableValue(false);
    const scrollYDelta = useMutableValue(0);
    const scrollDirection = useMutableValue(1);
    // Keep track of scroll direction
    useEffect(() => scrollY.on('change', (latestScrollY) => {
        const scrollingDown = latestScrollY > previousScrollY.current;
        if (dragging.get()) {
            scrollYDelta.set(0);
        }
        else {
            scrollYDelta.set(scrollYDelta.get() + Math.abs(latestScrollY - previousScrollY.current));
        }
        previousScrollY.current = latestScrollY;
        scrollDirection.set(scrollingDown ? ScrollDirection.down : ScrollDirection.up);
    }), [scrollDirection, scrollY, scrollYDelta, dragging]);
    useEffect(() => {
        return scrollDirection.on('change', () => {
            scrollYDelta.set(0);
        });
    }, [scrollYDelta, scrollDirection]);
    // Update window height
    useEffect(() => {
        const handleStart = () => {
            dragging.set(true);
        };
        const handleEnd = () => {
            dragging.set(false);
        };
        window.addEventListener('touchstart', handleStart);
        window.addEventListener('touchend', handleEnd);
        return () => {
            window.removeEventListener('touchstart', handleStart);
            window.removeEventListener('touchend', handleEnd);
        };
    }, [dragging]);
    return (React.createElement(ContentElementContext.Provider, { value: { contentRef, windowHeight, scrollY, scrollYDelta, scrollDirection } }, children));
};
export default ContentElementProvider;
